import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

// Use environment variable with fallback to current domain
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const HistorySimple = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalyses();
  }, []);

  const fetchAnalyses = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if user is logged in via Auth context
      if (!user) {
        console.log('No user in context, redirecting to login');
        setError('Please log in to view your history');
        setLoading(false);
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      // Get token from localStorage
      const token = localStorage.getItem('authToken');
      
      console.log('User:', user);
      console.log('Token exists:', !!token);
      console.log('API URL:', API);
      
      if (!token) {
        console.error('No token found in localStorage');
        setError('Session expired. Please log in again.');
        setLoading(false);
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      console.log('Fetching analyses from:', `${API}/analyses`);
      
      const response = await axios.get(`${API}/analyses`, {
        params: { limit: 100 },
        headers: {
          'Authorization': `Bearer ${token}`
        },
        withCredentials: true
      });

      console.log('Response status:', response.status);
      console.log('Analyses received:', response.data?.length || 0, 'items');
      setAnalyses(response.data || []);
    } catch (err) {
      console.error('Error fetching analyses:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      if (err.response?.status === 401) {
        // Clear invalid token
        localStorage.removeItem('authToken');
        localStorage.removeItem('userId');
        setError('Session expired. Please log in again.');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError(`Failed to load history: ${err.response?.data?.detail || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading history...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center px-6">
          <div className="text-red-400 text-xl mb-6">{error}</div>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => {
                setError(null);
                fetchAnalyses();
              }}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Retry
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Go to Login
            </button>
          </div>
          <div className="mt-6 text-gray-400 text-sm">
            <p>If the problem persists, try:</p>
            <p>1. Clear your browser cache</p>
            <p>2. Log out and log back in</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-2xl font-bold text-white">My Analyses</h1>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Back to Home
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {analyses.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-white text-xl mb-4">No analyses yet</div>
            <div className="text-gray-400 mb-6">Start analyzing content to see your history here</div>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Start Analyzing
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {analyses.map((analysis) => (
              <div
                key={analysis.id}
                onClick={() => navigate(`/analysis/${analysis.id}`)}
                className="bg-slate-800 border border-slate-700 rounded-lg p-6 hover:border-indigo-500 cursor-pointer transition-all"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex gap-3">
                    <span className="px-3 py-1 bg-indigo-600 text-white text-sm rounded-full">
                      {analysis.content_type}
                    </span>
                    <span className="px-3 py-1 bg-purple-600 text-white text-sm rounded-full">
                      {analysis.dominant_emotion}
                    </span>
                  </div>
                  <span className="text-gray-400 text-sm">
                    {new Date(analysis.timestamp).toLocaleDateString()}
                  </span>
                </div>
                <div className="text-gray-300 line-clamp-2">
                  {analysis.content || analysis.content_preview || 'Click to view details'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistorySimple;
